import { cn } from "@utils/tailwind.util";
import type { ComponentPropsWithoutRef, FunctionComponent, SVGProps } from "react";

export type IPillProps<T extends React.ElementType> = Omit<React.ComponentProps<T>, "as"> & {
  as?: T;
  children: React.ReactNode;
  color?: string;
  border?: boolean;
  size?: "sm" | "md" | "lg";
  icon?: FunctionComponent<SVGProps<SVGSVGElement>>;
  iconClass?: string;
};

type IBadgeProps<T extends React.ElementType> = Omit<ComponentPropsWithoutRef<"span">, "as"> & {
  as?: T;
  count: number;
};

function Badge<C extends React.ElementType = "span">({
  as,
  count,
  className,
  ...rest
}: IBadgeProps<C>) {
  const Component = as || "span";

  return (
    <Component
      className={cn(
        "inline-flex aspect-square flex-none items-center justify-center rounded-md align-middle text-xs font-medium leading-6 shadow-mainButton dark:bg-gray-600",
        className
      )}
      {...rest}
    >
      {count}
    </Component>
  );
}

export function Pill<C extends React.ElementType = "div">({
  as,
  iconClass,
  ...props
}: IPillProps<C>) {
  const { size, color, children, className, icon: Icon, border, ...rest } = props;

  const Component = as || "div";

  return (
    <Component
      className={cn(
        "inline-flex w-auto flex-none items-center gap-1 py-1 leading-none outline-none",
        {
          "rounded-lg px-1 ring-1 ring-inset ring-stroke-primary dark:ring-gray-700": border,
          "text-xs": size === "sm",
          "text-sm": size === "md",
          "text-base": size === "lg",
        },
        color,
        className
      )}
      {...rest}
    >
      {Icon && (
        <Icon className={cn("inline-block h-5 w-5 flex-none align-middle leading-5", iconClass)} />
      )}

      {typeof children === "string" ? (
        <span className="font-medium leading-none">{children}</span>
      ) : (
        children
      )}
    </Component>
  );
}

Pill.Badge = Badge;
